export type OfficeDocumentContext = {
  projectName: string;
  unitNumber: string;
  block: string;
  amountText: string;
  issueDate: string;
  allotteeNames: string[];
  primaryMobile: string;
  addressText: string;
};

export type OfficeDocumentTemplate = {
  id: string;
  name: string;
  description?: string;
  renderHtml: (ctx: OfficeDocumentContext) => string;
};

function esc(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export const allotmentLetterTemplate: OfficeDocumentTemplate = {
  id: 'allotment_letter_v1',
  name: 'Allotment Letter',
  description: 'Standard allotment letter draft for advocate review.',
  renderHtml: (ctx) => {
    const names = ctx.allotteeNames.length ? ctx.allotteeNames.join(', ') : '________________';
    const address = ctx.addressText || '________________';
    return `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Allotment Letter</title>
  <style>
    @page { size: A4; margin: 18mm; }
    body { font-family: "Times New Roman", serif; color: #111; line-height: 1.55; font-size: 14px; }
    .header { text-align: center; margin-bottom: 16px; }
    .title { font-size: 24px; font-weight: 700; letter-spacing: 0.2px; margin-bottom: 4px; }
    .meta { display: flex; justify-content: space-between; margin: 10px 0 16px; }
    .block { border: 1px solid #ddd; border-radius: 8px; padding: 10px 12px; margin: 8px 0; }
    .label { color: #475569; font-size: 12px; display: block; margin-bottom: 2px; }
    .value { font-size: 15px; font-weight: 600; }
    .para { margin: 10px 0; text-align: justify; }
    .sig-wrap { margin-top: 36px; display: flex; justify-content: space-between; }
    .sig { width: 40%; text-align: center; }
    .line { border-top: 1px solid #333; margin-top: 38px; padding-top: 6px; }
    .note { margin-top: 18px; color: #475569; font-size: 12px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="title">ALLOTMENT LETTER</div>
    <div>${esc(ctx.projectName)}</div>
  </div>

  <div class="meta">
    <div>Unit No: <strong>${esc(ctx.unitNumber)}</strong></div>
    <div>Date: <strong>${esc(ctx.issueDate)}</strong></div>
  </div>

  <div class="block">
    <span class="label">Allottee Name(s)</span>
    <span class="value">${esc(names)}</span>
  </div>
  <div class="block">
    <span class="label">Address</span>
    <span class="value">${esc(address)}</span>
  </div>
  <div class="block">
    <span class="label">Mobile</span>
    <span class="value">${esc(ctx.primaryMobile || '—')}</span>
  </div>
  <div class="block">
    <span class="label">Unit / Block</span>
    <span class="value">${esc(ctx.unitNumber)}${ctx.block ? ` / ${esc(ctx.block)}` : ''}</span>
  </div>
  <div class="block">
    <span class="label">Consideration Amount</span>
    <span class="value">${esc(ctx.amountText)}</span>
  </div>

  <p class="para">
    This is to confirm that the above unit has been allotted in favour of the allottee(s) mentioned above,
    subject to terms, conditions, payment schedule and documentation formalities as applicable.
  </p>
  <p class="para">
    This draft is prepared for advocate processing of agreement and associated legal documentation.
  </p>

  <div class="sig-wrap">
    <div class="sig">
      <div class="line">Authorized Signatory</div>
    </div>
    <div class="sig">
      <div class="line">Allottee(s)</div>
    </div>
  </div>

  <div class="note">
    System generated draft for workflow processing.
  </div>
</body>
</html>`;
  },
};

export const officeDocumentTemplates: OfficeDocumentTemplate[] = [allotmentLetterTemplate];

export function openTemplatePrintPreview(template: OfficeDocumentTemplate, ctx: OfficeDocumentContext) {
  const html = template.renderHtml(ctx);
  const w = window.open('', '_blank', 'noopener,noreferrer');
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  setTimeout(() => {
    try {
      w.focus();
      w.print();
    } catch {
    }
  }, 200);
  return true;
}

